<template>
  <div class="task">
    <div v-if="!expanded" class="task-trigger" @click="expand">
      <i class="fa-regular fa-circle-plus task-trigger-icon"></i>
      <span>{{ triggerLabel }}</span>
    </div>

    <div v-else class="task-form">
      <i class="fa-regular fa-circle task-bullet"></i>

      <div class="task-fields">
        <input
          ref="titleInput"
          v-model="task.subject"
          type="text"
          class="task-input task-title"
          placeholder="Título"
          maxlength="100"
        />
        <input
          v-model="task.details"
          type="text"
          class="task-input task-details"
          placeholder="Detalhes"
        />

        <div class="task-pills">
          <div class="task-pill-wrapper" ref="dateWrapper">
            <button
              type="button"
              class="task-pill"
              :class="{ 'task-pill-filled': hasDateTime }"
              @click="toggleDatePicker"
            >
              <i class="fa-regular fa-calendar"></i>
              <span>{{ dateTimeLabel }}</span>
            </button>

            <div v-if="showDatePicker" class="task-popover">
              <DateTime
                title=""
                field="date"
                formName="taskComposeForm"
                format="DD/MM/YYYY"
                type="date"
                class="task-date-field"
                :appendToBody="true"
                :popupStyle="datePickerPopupStyle"
                v-model="task.dateStartEnd"
              />
              <DateTime
                title=""
                field="time"
                formName="taskComposeForm"
                format="HH:mm"
                type="time"
                class="task-time-field"
                :appendToBody="true"
                :popupStyle="datePickerPopupStyle"
                v-model="task.timeStartEnd"
              />
            </div>
          </div>

          <div class="task-pill-wrapper" ref="assignWrapper">
            <button
              type="button"
              class="task-pill"
              :class="{ 'task-pill-filled': assignedUser.id }"
              @click="toggleAssign"
            >
              <img
                v-if="assignedUser.photo"
                :src="assignedPhotoUrl"
                class="task-pill-avatar"
                alt=""
              />
              <i v-else class="fa-regular fa-user"></i>
              <span>{{ assignLabel }}</span>
            </button>

            <div v-if="showAssignDropdown" class="task-popover task-popover-users">
              <div
                v-for="user in users"
                :key="user.id"
                class="task-user-option"
                @click="selectUser(user)"
              >
                <img :src="userPhotoUrl(user.any)" class="task-user-option-avatar" alt="" />
                <span class="task-user-option-name">{{ user.content }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="task-actions">
          <button
            type="button"
            class="task-action task-save"
            :class="{ 'task-save-active': !isInvalid }"
            :disabled="isInvalid || isLoading('btnSaveTask')"
            @click="save"
          >
            Salvar
          </button>
          <button
            type="button"
            class="task-action task-cancel"
            @click="cancel"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateTime from "@/ui/forms/DateTime";

import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

const PHOTO_BASE_URL = "https://espaco.blob.core.windows.net/nixloc-photo-user/";

export default {
  name: "Task",
  components: { DateTime },
  props: {
    entityId: {
      type: String,
      required: true,
    },
    createTask: {
      type: Function,
      required: true,
    },
    updateTask: {
      type: Function,
      required: true,
    },
    ruleName: {
      type: String,
      default: "CRM",
    },
    editEventName: {
      type: String,
      default: "taskEdit",
    },
    savedEventName: {
      type: String,
      default: "taskSaved",
    },
    triggerLabel: {
      type: String,
      default: "Adicionar tarefa",
    },
  },
  data() {
    return {
      expanded: false,
      showDatePicker: false,
      showAssignDropdown: false,
      users: [],
      assignedUser: { id: "", photo: "", name: "" },
      task: this.emptyTask(),
      datePickerPopupStyle: { zIndex: 3000 },
    };
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick, true);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick, true);
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    ...mapGetters("generic", ["event", "isLoading"]),
    isInvalid() {
      return !this.task.subject;
    },
    hasDateTime() {
      return !!this.task.dateStartEnd;
    },
    dateTimeLabel() {
      if (!this.hasDateTime) return "Adicionar data/hora";
      return this.task.timeStartEnd ? `${this.task.dateStartEnd} ${this.task.timeStartEnd}` : this.task.dateStartEnd;
    },
    assignLabel() {
      return this.assignedUser.name || "Atribuir";
    },
    assignedPhotoUrl() {
      return PHOTO_BASE_URL + this.assignedUser.photo;
    },
  },
  methods: {
    ...mapMutations("generic", ["addEvent", "addLoading", "removeLoading"]),
    ...mapActions("generic", ["getApi"]),
    emptyTask() {
      return {
        id: "",
        subject: "",
        details: "",
        dateStartEnd: "",
        timeStartEnd: "",
        userId: "",
      };
    },
    expand() {
      this.expanded = true;
      this.loadUsers();
      this.$nextTick(() => this.$refs.titleInput && this.$refs.titleInput.focus());
    },
    toggleDatePicker() {
      this.showDatePicker = !this.showDatePicker;
      this.showAssignDropdown = false;
    },
    toggleAssign() {
      this.showAssignDropdown = !this.showAssignDropdown;
      this.showDatePicker = false;
      if (this.showAssignDropdown && this.users.length === 0) this.loadUsers();
    },
    handleOutsideClick(event) {
      if (this.showDatePicker) {
        const insideWrapper = this.$refs.dateWrapper && this.$refs.dateWrapper.contains(event.target);
        const insideCalendarPopup = event.target.closest(".mx-datepicker-popup");
        if (!insideWrapper && !insideCalendarPopup) this.showDatePicker = false;
      }
      if (this.showAssignDropdown) {
        const insideWrapper = this.$refs.assignWrapper && this.$refs.assignWrapper.contains(event.target);
        if (!insideWrapper) this.showAssignDropdown = false;
      }
    },
    async loadUsers() {
      const params = {
        url: "/api/v1/adm/user/select-all-by-rule-name",
        obj: { search: "", currentPage: 1, totalPerPage: 20, any: this.ruleName },
      };
      const response = await this.getApi(params);
      this.users = response.content.data;
    },
    userPhotoUrl(photo) {
      return PHOTO_BASE_URL + (photo || "noImage.png");
    },
    selectUser(user) {
      this.assignedUser = { id: user.id, photo: user.any, name: user.content };
      this.task.userId = user.id;
      this.showAssignDropdown = false;
    },
    async save() {
      this.addLoading("btnSaveTask");
      const payload = {
        ...this.task,
        entityId: this.entityId,
        userId: this.task.userId || this.userLogged.user.id,
      };
      try {
        if (payload.id) {
          await this.updateTask(payload);
        } else {
          await this.createTask(payload);
        }
        this.addEvent({ name: this.savedEventName });
        this.reset();
      } finally {
        this.removeLoading(["btnSaveTask"]);
      }
    },
    cancel() {
      this.reset();
    },
    reset() {
      this.task = this.emptyTask();
      this.assignedUser = { id: "", photo: "", name: "" };
      this.showDatePicker = false;
      this.showAssignDropdown = false;
      this.expanded = false;
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name === this.editEventName) {
          this.task.id = event.data.id;
          this.task.subject = event.data.subject;
          this.task.dateStartEnd = event.data.dateStart;
          this.task.timeStartEnd = event.data.timeStart;
          this.assignedUser = {
            id: event.data.userId || "",
            photo: event.data.photo || "",
            name: event.data.registeredUser || "",
          };
          this.expanded = true;
          this.loadUsers();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.task {
  margin-bottom: 16px;
}

.task-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
}

.task-trigger:hover {
  background: var(--bg-hover);
}

.task-trigger-icon {
  font-size: 16px;
  color: var(--text-muted);
}

.task-form {
  display: flex;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--bg-base);
}

.task-bullet {
  margin-top: 4px;
  font-size: 15px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.task-fields {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.task-input {
  border: none !important;
  background: transparent !important;
  outline: none;
  width: 100%;
  color: var(--text-primary) !important;
  padding: 2px 0;
  font-weight: inherit !important;
}

.task-input:hover {
  background: transparent !important;
}

.task-input::placeholder {
  color: var(--text-muted);
}

.task-title {
  font-size: 14px;
  font-weight: 500;
}

.task-details {
  font-size: 13px;
  font-style: italic;
  color: var(--text-secondary);
}

.task-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}

.task-pill-wrapper {
  position: relative;
}

.task-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 3px 12px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--bg-elevated);
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.task-pill:hover {
  background: var(--bg-hover);
}

.task-pill-filled {
  color: var(--color-brand);
  border-color: var(--color-brand);
}

.task-pill-avatar {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  object-fit: cover;
}

.task-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1000;
  display: flex;
  gap: 8px;
  padding: 12px;
  min-width: 300px;
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
}

.task-date-field {
  flex: 1 1 190px;
}

.task-time-field {
  flex: 0 0 110px;
}

.task-popover-users {
  flex-direction: column;
  min-width: 220px;
  max-height: 260px;
  overflow-y: auto;
}

.task-user-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
}

.task-user-option:hover {
  background: var(--bg-hover);
}

.task-user-option-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  object-fit: cover;
  flex-shrink: 0;
}

.task-user-option-name {
  font-size: 13px;
  color: var(--text-primary);
}

.task-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
}

.task-action {
  border: none;
  background: none;
  padding: 6px 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.task-save {
  color: var(--text-muted);
  cursor: not-allowed;
}

.task-save-active {
  color: var(--color-brand);
  cursor: pointer;
}

.task-cancel {
  color: var(--color-danger);
}
</style>

